import { useI18n } from "hooks/i18n/i18n";
import { getTicketClass } from "pages/AccountSettlementHistoryOrderId/utils/getTicketClass";
import { useMemo } from "react";

import { PRODUCT_TYPE } from "../../constants/ProductType";
import { getProductType } from "../../utils/getProductType";
import { getReleaseDate } from "../../utils/getReleaseDate";
import { getTicketStatus } from "../../utils/getTicketStatus";

import type { ProductItem } from "../../types/ProductItem";
import type { TimeStamp } from "../../types/TimeStamp";
import type { Store } from "store";

type EventPart = {
  name: string;
  openTime: TimeStamp;
};

type UseOrderListInput = {
  transaction?: Store["purchase"]["activeTransactionMap"][string];
  eventImageMap: Record<string, string>;
  eventPartsMap: Record<string, EventPart[]>;
  bonusMap: Record<string, boolean> | null;
  isImageLoading: boolean;
};

type UseOrderListOutput = {
  orderList: ProductItem[];
};

const formatProductVariationName = (
  productName: string,
  subClassName?: string
): string => {
  if (subClassName && subClassName !== "") {
    return `${productName}(${subClassName})`;
  }
  return productName;
};

export const useOrderList = (input: UseOrderListInput): UseOrderListOutput => {
  const {
    transaction,
    eventImageMap,
    eventPartsMap,
    bonusMap,
    isImageLoading,
  } = input;
  const { t } = useI18n();

  const orderList: ProductItem[] = useMemo(() => {
    if (!transaction || !transaction.products) return [];
    return transaction.products
      .map((product): ProductItem | null => {
        const type = getProductType(product);

        if (type === PRODUCT_TYPE.TICKET) {
          const eventParts = eventPartsMap[product.eid] ?? [];
          const { firstPartTime, lastPartTime, status } =
            getTicketStatus(eventParts);
          const displayFirstPartTime = firstPartTime;

          return {
            eventTitle: product.eventTitle,
            productName: product.eventLongTitle,
            variationName: product.name,
            price: product.price_jpy,
            quantity: product.count,
            type,
            imageSrc: eventImageMap[product.eid] ?? "",
            isImageLoading,
            liveTicketInfo: {
              ...getReleaseDate(displayFirstPartTime, lastPartTime, t),
              status,
              eventId: product.eid,
              ticketId: product.ref.id,
              class: getTicketClass(product.class),
              hasBonusMedia: bonusMap?.[product.ref.id] === true,
            },
          };
        }
        return {
          eventTitle: product.eventTitle,
          productName: product.eventLongTitle,
          variationName: formatProductVariationName(
            product.name,
            product.subClassName
          ),
          price: product.price_jpy,
          quantity: product.count,
          type,
          imageSrc: product.thumbnail ?? "",
          isImageLoading: false,
        };
      })
      .filter((item): item is ProductItem => item !== null);
  }, [eventImageMap, eventPartsMap, t, transaction, bonusMap, isImageLoading]);

  return { orderList };
};
